/* eslint-disable react/no-unescaped-entities */
import React from 'react'
import './index.scss'
import Layout from '../../components/BridgePage/Layout'

const AboutTs = () => (
  <Layout>
    <div className="tsContainer">
      <h1 className="tsTitle">Yaaro User Agreement</h1>

      <p><strong>Effective Date:</strong> May 15, 2026</p>

      <h2>Section 1: Introduction and Acceptance of Terms</h2>
      <p>
        Yaaro welcomes you to its platform, which includes websites, mobile applications, and associated services or products. This Agreement governs your use of Yaaro's offerings and your interactions with the platform. By accessing or using Yaaro's services, you agree to comply with the terms set out below, as well as any additional rules or conditions that apply to specific features or paid services. Those supplementary provisions form an integral part of this Agreement. If you do not agree with any part of these terms or Yaaro's Privacy Policy, you must immediately stop using the platform.
      </p>
      <p>
        While using Yaaro's services, you may come across content generated by third parties or other users. Yaaro does not take responsibility for the accuracy, suitability, or legality of such content. Moreover, you accept the possibility of encountering offensive or incorrect material, and you waive any claims against Yaaro arising from your exposure to that content.
      </p>
      <p>
        Yaaro's services are not available to individuals or entities located in restricted regions, including Cuba, Iran, North Korea, Sudan, Syria, the Crimean region, or areas designated as DNR or LNR. Users in those locations are forbidden from accessing the platform.
      </p>
      <p>
        We reserve the right to update these terms from time to time – for example, when new features are added, services are merged, or legal requirements change. Notice of significant changes will be posted on the platform, and the "Effective Date" will reflect the latest revision. Your continued use of Yaaro after such changes constitutes your acceptance of the updated terms.
      </p>
      <p>
        When using the platform, you must follow Yaaro's Privacy Policy, Community Guidelines, and Child Safety Policy. Additional rules may apply to specific features or services, and those terms will be presented together with those offerings. For paid services, extra terms will also be disclosed during the purchase process.
      </p>
      <p>
        The service is not available to anyone under the age of 18. By using the service, you confirm that:
      </p>
      <ul>
        <li>You are at least the "Minimum Age" (as defined below).</li>
        <li>Yaaro has not previously restricted your access to the service.</li>
      </ul>
      <p>
        Creating an account with false information – including accounts registered on behalf of another person or anyone under 18 – violates this Agreement.
      </p>
      <p>
        The "Minimum Age" is defined as 18 years of age. Notwithstanding the foregoing, if the laws of your jurisdiction require a higher age for Yaaro to lawfully provide the Services (including processing your personal data) without parental consent, the Minimum Age shall be the age stipulated by your local laws.
      </p>

      <h2>Section 2: User Obligations and Prohibited Conduct</h2>
      <p>
        When using Yaaro, you agree to act responsibly, lawfully, and respectfully toward other users and the platform's integrity. The following activities are strictly forbidden:
      </p>
      <ul>
        <li>Attempting to access other users' accounts or Yaaro's systems without authorisation.</li>
        <li>Uploading, transmitting, or distributing any harmful code, malware, or malicious software.</li>
        <li>Posting or sharing defamatory, obscene, discriminatory, or violent material.</li>
        <li>Engaging in harassment, stalking, fraud, impersonation, or any illegal activity.</li>
      </ul>
      <p>
        Yaaro reserves the right to suspend or terminate accounts, restrict access, report unlawful behaviour to authorities, and pursue legal action against any user who violates these terms.
      </p>

      <h2>Section 3: Payments and Billing Policy</h2>
      <p>
        Yaaro accepts major credit cards, select debit cards, and other payment methods as may be introduced from time to time. When you make a purchase (e.g., virtual items, subscriptions, or other paid features), you authorise Yaaro or its payment processors to charge your selected payment method for the full amount. You may be required to provide additional information (such as government-issued identification) to complete a transaction or comply with legal obligations.
      </p>
      <p>
        If a payment is declined, you should verify your payment details and retry. For assistance, please contact our support team at the address provided in Section 12.
      </p>

      <h2>Section 4: User Accounts and Security</h2>
      <p>
        To access certain features of the Platform, you must register an account providing accurate, current, and complete information. Usernames that are offensive, misleading, or impersonate others are prohibited and may result in account termination.
      </p>
      <p>
        You are solely responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify Yaaro immediately of any unauthorised access to or use of your account.
      </p>
      <p>
        If you wish to permanently delete your account, you may contact us at <a href="mailto:report@yaarolive.com">report@yaarolive.com</a>. Account deletion is irreversible, and your data cannot be recovered thereafter.
      </p>

      <h2>Section 5: User-Generated Content (UGC) – License and Rules</h2>
      <p>
        By submitting any user-generated content through the Yaaro platform, you grant Yaaro and its sublicensees an irrevocable, perpetual, non-exclusive, royalty-free, worldwide licence to use, modify, distribute, publicly display, and create derivative works of such content.
      </p>
      <p>In addition, you agree to the following:</p>
      <ul>
        <li>
          <strong>Legality and Intellectual Property:</strong> Your UGC must not infringe any third party's copyright, trademark, trade secret, or other proprietary rights. You must have obtained all necessary permissions or legal authority to use any protected material incorporated into your content.
        </li>
        <li>
          <strong>Prohibited Content:</strong> UGC must not contain illegal, defamatory, obscene, violent, hateful, or harassing material, nor promote unlawful activities, self-harm, or endanger public safety.
        </li>
        <li>
          <strong>Accuracy:</strong> You are responsible for the truthfulness and accuracy of your UGC. Knowingly submitting false or misleading information is prohibited.
        </li>
        <li>
          <strong>Platform Integrity:</strong> UGC that harms Yaaro's reputation, disrupts the user experience, or compromises the platform's security or integrity is forbidden.
        </li>
      </ul>
      <p>
        Yaaro does not assume liability for user-generated content but reserves the right to review, moderate, remove, or restrict access to any UGC that violates this Agreement. Upon identifying a violation, Yaaro may:
      </p>
      <ul>
        <li>Remove or limit access to the offending content.</li>
        <li>Suspend or terminate the responsible account(s).</li>
        <li>Report illegal activities to appropriate law enforcement authorities.</li>
      </ul>
      <p>
        By submitting UGC, you acknowledge that Yaaro may use your content for promotional purposes, technical testing, or other lawful internal activities without further notice or compensation to you.
      </p>

      <h2>Section 6: Third-Party Services and Mobile Access</h2>
      <p>
        Your use of any third-party services, websites, or applications that are accessible through the Yaaro Platform is governed solely by the terms and privacy policies of those third parties. Yaaro is not responsible or liable for the practices, content, or functionality of any third-party service.
      </p>
      <p>
        If you access Yaaro via a mobile device, standard carrier messaging, data, and other fees may apply. You are solely responsible for any such charges.
      </p>

      <h2>Section 7: Content Moderation and Enforcement</h2>
      <p>
        To maintain a safe and respectful environment, Yaaro reserves the right to:
      </p>
      <ul>
        <li>Monitor, moderate, or remove any content that violates this Agreement or applicable law.</li>
        <li>Cooperate with law enforcement authorities in investigating illegal activities.</li>
        <li>Limit, suspend, or terminate access to the Platform for any user who breaches these terms.</li>
      </ul>
      <p>
        While Yaaro strives to enforce its rules, we cannot guarantee the immediate or complete removal of all harmful or inappropriate content. Users are encouraged to report violations to <a href="mailto:report@yaarolive.com">report@yaarolive.com</a>.
      </p>

      <h2>Section 8: Intellectual Property Rights</h2>
      <p>
        All trademarks, logos, service marks, trade names, and proprietary content (including but not limited to software, graphics, and layout) displayed on the Yaaro Platform are the exclusive property of Yaaro or its licensors. You are granted a limited, revocable, non-exclusive, non-transferable license to access and use the Platform for your personal, non-commercial use. Any unauthorized reproduction, modification, distribution, or creation of derivative works is strictly prohibited.
      </p>

      <h2>Section 9: Disclaimer of Warranties and Limitation of Liability</h2>
      <p>
        Yaaro's services are provided “as is” without warranties of any kind. To the fullest extent permitted by law, Yaaro disclaims all liability for damages, including lost profits or data. Yaaro's total liability is limited to the amount paid by you for the services, if any.
      </p>

      <h2>Section 10: Indemnification</h2>
      <p>
        You agree to indemnify and hold Yaaro harmless from any claims, damages, or legal fees arising out of your use of the platform, your violation of this Agreement, or your infringement of any third-party rights.
      </p>

      <h2>Section 11: Governing Law and Dispute Resolution</h2>
      <p>
        This Agreement is governed by the laws of Singapore. Any dispute will first be addressed through good-faith negotiations. If the dispute is not resolved within 30 days, it shall be referred to the Singapore International Arbitration Centre (SIAC) for arbitration, with proceedings conducted in English. The arbitration award shall be final and binding on both parties.
      </p>

      <h2>Section 12: General Provisions</h2>
      <p>
        Our failure to enforce any provision of this Agreement does not constitute a waiver of that provision. If any term is found to be unenforceable, the remaining terms will remain in full force and effect. This Agreement constitutes the entire understanding between you and Yaaro regarding your use of the platform.
      </p>

      <p><strong>Contact Information:</strong></p>
      <p>
        For any questions, notices, or reports of violations, please contact Yaaro at:
        <br />
        Email: <a href="mailto:report@yaarolive.com">report@yaarolive.com</a>
      </p>
    </div>
  </Layout>
)

export default AboutTs
